// Turns stored area geometry into SVG outline paths for the small thumbnails on the Home cards and the
// Saved Areas list, fitted into a box (and, with thumbnailView, matched to a MapLibre basemap view). The view
// itself is framed by the area shapes module. Pure: no React, router or services.
import { WORLD_TILE, fitView, lngLatToWorld } from '../geo/areaShapes';
import type { AreaShape } from '../geo/areaShapes';

export type Coord = [number, number]; // lng, lat

export interface Shape {
  kind: 'polygon' | 'line';
  rings: Coord[][];
}

export interface FitOptions {
  width: number;
  height: number;
  pad: number;
}

export interface FittedPath {
  d: string;
  kind: Shape['kind'];
}

/** A ring longer than this is thinned before it is drawn. */
export const MAX_RING_POINTS = 200;
/** The side of the marker drawn for an area that has no size, in box units. */
const POINT_MARKER = 8;

const isCoord = (c: unknown): c is Coord =>
  Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]);

function cleanRing(ring: unknown): Coord[] {
  return Array.isArray(ring) ? ring.filter(isCoord).map((c) => [c[0], c[1]] as Coord) : [];
}

function cleanRings(rings: unknown): Coord[][] {
  return Array.isArray(rings) ? rings.map(cleanRing).filter((r) => r.length > 0) : [];
}

/** Outlines of a GeoJSON geometry: Polygon and MultiPolygon as polygons, LineString and MultiLineString as lines. */
export function shapesFromGeoJSON(geom: unknown): Shape[] {
  if (!geom || typeof geom !== 'object') return [];
  const { type, coordinates } = geom as { type?: unknown; coordinates?: unknown };
  if (!Array.isArray(coordinates)) return [];

  switch (type) {
    case 'Polygon': {
      const rings = cleanRings(coordinates);
      return rings.length > 0 ? [{ kind: 'polygon', rings }] : [];
    }
    case 'MultiPolygon':
      return coordinates
        .map((poly) => cleanRings(poly))
        .filter((rings) => rings.length > 0)
        .map((rings) => ({ kind: 'polygon' as const, rings }));
    case 'LineString': {
      const ring = cleanRing(coordinates);
      return ring.length > 0 ? [{ kind: 'line', rings: [ring] }] : [];
    }
    case 'MultiLineString': {
      const rings = cleanRings(coordinates);
      return rings.length > 0 ? [{ kind: 'line', rings }] : [];
    }
    default:
      return [];
  }
}

/** At most MAX_RING_POINTS points, evenly spread, keeping the first and the last. */
function decimate(ring: Coord[]): Coord[] {
  if (ring.length <= MAX_RING_POINTS) return ring;
  const out: Coord[] = [];
  for (let k = 0; k < MAX_RING_POINTS; k++) {
    out.push(ring[Math.round((k * (ring.length - 1)) / (MAX_RING_POINTS - 1))]);
  }
  return out;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * SVG path data for the shapes, fitted into the box. Equirectangular projection with x scaled by
 * cos(mean latitude) and y flipped; one scale for all shapes so they keep their places relative to each
 * other, centred, with `pad` left free on every side. Null when there are no coordinates. An area with no
 * size at all gets a small marker so it can still be seen.
 */
export function fitShapes(shapes: Shape[], { width, height, pad }: FitOptions): FittedPath[] | null {
  const all = shapes.flatMap((s) => s.rings.flat());
  if (all.length === 0) return null;

  const meanLat = all.reduce((sum, c) => sum + c[1], 0) / all.length;
  const kx = Math.cos((meanLat * Math.PI) / 180);
  const project = (c: Coord): Coord => [c[0] * kx, -c[1]];

  const projected = all.map(project);
  const minX = Math.min(...projected.map((p) => p[0]));
  const maxX = Math.max(...projected.map((p) => p[0]));
  const minY = Math.min(...projected.map((p) => p[1]));
  const maxY = Math.max(...projected.map((p) => p[1]));
  const spanX = maxX - minX;
  const spanY = maxY - minY;

  const innerW = Math.max(width - 2 * pad, 1);
  const innerH = Math.max(height - 2 * pad, 1);
  const cx = width / 2;
  const cy = height / 2;

  if (spanX === 0 && spanY === 0) {
    const h = POINT_MARKER / 2;
    return [
      {
        kind: 'polygon',
        d: `M${round1(cx)} ${round1(cy - h)}L${round1(cx + h)} ${round1(cy)}L${round1(cx)} ${round1(cy + h)}L${round1(cx - h)} ${round1(cy)}Z`,
      },
    ];
  }

  // A line that is exactly level or upright has one zero span; only the other one limits the scale.
  const scale = Math.min(spanX > 0 ? innerW / spanX : Infinity, spanY > 0 ? innerH / spanY : Infinity);
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const toBox = (c: Coord): string => {
    const [px, py] = project(c);
    return `${round1(cx + (px - midX) * scale)} ${round1(cy + (py - midY) * scale)}`;
  };

  const paths: FittedPath[] = [];
  for (const shape of shapes) {
    for (const ring of shape.rings) {
      if (ring.length === 0) continue;
      const pts = decimate(ring);
      const d = `M${pts.map(toBox).join('L')}${shape.kind === 'polygon' ? 'Z' : ''}`;
      paths.push({ d, kind: shape.kind });
    }
  }
  return paths;
}

// ---- Web Mercator fit, for thumbnails drawn over a MapLibre basemap ----

export interface MapView {
  center: Coord;
  zoom: number;
  paths: FittedPath[];
}

/** The shapes as GeoJSON, for the framing. */
function toAreaShapes(shapes: Shape[]): AreaShape[] {
  return shapes.flatMap((s): AreaShape[] =>
    s.kind === 'polygon' ? [{ type: 'Polygon', coordinates: s.rings }] : [{ type: 'MultiLineString', coordinates: s.rings }],
  );
}

/**
 * Centre, zoom and outline paths (box pixels) for the shapes in a box, in Web Mercator. The view is the one
 * the area shapes module frames for the box (bounding box padded and centred, fractional zoom 3 to 14). The
 * paths use exactly the projection a MapLibre map has after jumpTo({ center, zoom }), so the outline lands on
 * the rendered basemap. Null when there are no coordinates. An area with no size gets a
 * small marker.
 */
export function thumbnailView(shapes: Shape[], width: number, height: number): MapView | null {
  const all = shapes.flatMap((s) => s.rings.flat());
  if (all.length === 0) return null;

  const world = all.map(lngLatToWorld);
  const xs = world.map((p) => p[0]);
  const ys = world.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const view = fitView(toAreaShapes(shapes), null, { width, height });
  const zoom = view.zoom;
  const center: Coord = [view.longitude, view.latitude];
  const [cx, cy] = lngLatToWorld(center);

  const scale = WORLD_TILE * 2 ** zoom; // world units to pixels
  const toBox = (w: Coord): Coord => [width / 2 + (w[0] - cx) * scale, height / 2 + (w[1] - cy) * scale];
  const fmt = (p: Coord) => `${round1(p[0])} ${round1(p[1])}`;

  const paths: FittedPath[] = [];
  if (maxX === minX && maxY === minY) {
    const h = POINT_MARKER / 2;
    const [px, py] = toBox([cx, cy]);
    paths.push({
      kind: 'polygon',
      d: `M${round1(px)} ${round1(py - h)}L${round1(px + h)} ${round1(py)}L${round1(px)} ${round1(py + h)}L${round1(px - h)} ${round1(py)}Z`,
    });
    return { center, zoom, paths };
  }
  for (const shape of shapes) {
    for (const ring of shape.rings) {
      if (ring.length === 0) continue;
      const pts = decimate(ring).map((c) => fmt(toBox(lngLatToWorld(c))));
      paths.push({ d: `M${pts.join('L')}${shape.kind === 'polygon' ? 'Z' : ''}`, kind: shape.kind });
    }
  }
  return { center, zoom, paths };
}
